import { useEffect, useRef } from "react";

interface UseSEOOptions {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function useSEO(options: UseSEOOptions) {
  const prevRef = useRef<string>("");

  useEffect(() => {
    const {
      title,
      description,
      keywords,
      canonicalUrl,
      ogTitle,
      ogDescription,
      ogImage,
      ogType = "website",
      jsonLd,
    } = options;

    const currentKey = title + description;
    if (prevRef.current === currentKey) return;
    prevRef.current = currentKey;

    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
    }

    const ogTitleVal = ogTitle || title;
    const ogDescVal = ogDescription || description;
    const siteUrl = "https://hanhanmanhua.com";

    setMeta("og:title", ogTitleVal, true);
    setMeta("og:description", ogDescVal, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl, true);
    setMeta("og:site_name", "汗汗漫画", true);
    setMeta("og:locale", "zh_CN", true);
    if (ogImage) setMeta("og:image", ogImage, true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", ogTitleVal);
    setMeta("twitter:description", ogDescVal);
    if (ogImage) setMeta("twitter:image", ogImage);

    let scriptEl = document.querySelector('script[data-jsonld="true"]');
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.setAttribute("type", "application/ld+json");
        scriptEl.setAttribute("data-jsonld", "true");
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(
        Array.isArray(jsonLd) ? jsonLd : jsonLd,
        null,
        0
      );
    } else if (scriptEl) {
      scriptEl.remove();
    }
  }, [options]);
}